/**
 * Centralized route catalog.
 *
 * Single source of truth for every frontend URL in the app. All
 * `createRoute({ path })`, `getRouteApi(...)`, `<Link to=...>`,
 * `<Navigate to=...>`, and `navigate({ to })` callers MUST reference
 * `ROUTES.<...>.path` (router param token) or `ROUTES.<...>.id`
 * (registered route id, used by `getRouteApi`).
 *
 * Runtime URLs built from a slug VALUE (not a router param token) MUST
 * use `routeUrl.<...>(slug, ...)`. Examples: notification deep-links,
 * `window.location.assign(...)`.
 *
 * Why one file: changing the workspace prefix (e.g. `/$workspaceSlug` to
 * `/w/$workspaceSlug`), renaming a route segment, or adding a new route
 * is a 1-line edit here. TypeScript validates every consumer via the
 * `as const` literal types preserved through `app()` and `ws()`.
 *
 * The audit (`scripts/audit.sh` rules RT1, RT2) treats this file and the
 * named pathname helpers (`use-active-workspace.tsx`, `topbar.tsx`) as
 * the only allowlist for literal workspace URL prefixes.
 *
 * Vite dev proxy mirrors `WORKSPACE_API_BASE` from `src/shared/lib/api-paths.ts`.
 */

const APP_LAYOUT = "/app-layout" as const;
const AUTH_LAYOUT = "/auth-layout" as const;
const PRINT_LAYOUT = "/print-layout" as const;

/** First path segment for every workspace-scoped route. Owns the prefix. */
export const WORKSPACE_BASE = "/$workspaceSlug" as const;

function app<S extends string>(sub: S) {
  return {
    path: sub,
    id: `${APP_LAYOUT}${sub}` as const,
  };
}

function ws<S extends string>(sub: S) {
  return {
    path: `${WORKSPACE_BASE}${sub}` as const,
    id: `${APP_LAYOUT}${WORKSPACE_BASE}${sub}` as const,
  };
}

/**
 * Workspace-scoped routes rendered OUTSIDE the app shell: no sidebar, no
 * topbar, no toasts. Only the print views use this today (HRMS-PAY-023, the
 * print-HTML archetype from `attachments-and-exports.md` section 9), where the
 * page IS the document and every piece of app chrome would print.
 *
 * The `path` is the same `/$workspaceSlug/...` shape as `ws()`; only the
 * registered `id` differs, because these hang off a different layout route.
 */
function wsPrint<S extends string>(sub: S) {
  return {
    path: `${WORKSPACE_BASE}${sub}` as const,
    id: `${PRINT_LAYOUT}${WORKSPACE_BASE}${sub}` as const,
  };
}

function auth<S extends string>(sub: S) {
  return {
    path: sub,
    id: `${AUTH_LAYOUT}${sub}` as const,
  };
}

export const ROUTES = {
  root: app("/"),
  designSystem: app("/design-system"),
  profile: app("/profile"),
  /// The unified settings shell: one screen with a left rail, replacing the
  /// scattered set of settings-ish pages a user previously had to already
  /// know the URLs of. Section pages live under it as real paths (not a
  /// search param) so each is linkable and the browser Back button steps
  /// between them.
  settings: app("/settings"),
  settingsProfile: app("/settings/profile"),
  /// Personal access tokens: each user mints and revokes their own, so no
  /// role gate (the shore scopes every call to the caller).
  settingsTokens: app("/settings/tokens"),
  /// Connect an AI tool over MCP and manage the caller's own connected apps.
  /// Every user, so no role gate (the shore scopes each call to the caller).
  settingsMcp: app("/settings/mcp"),
  /// The MCP page's first path; redirects into settingsMcp.
  settingsAiTools: app("/settings/ai-tools"),
  /// Account-wide AI app connections and the on/off switch. Owner or admin.
  settingsAiConnections: app("/settings/ai-connections"),
  settingsAccount: app("/settings/account"),
  settingsBilling: app("/settings/billing"),
  /// Kept for existing links and bookmarks; redirects into settingsAccount.
  accountSettings: app("/account/settings"),
  /// Authenticated, owner-only plan + purchase history summary (task 5 of
  /// the billing surfaces plan, `GET /billing/me`). Account-level, not
  /// workspace-scoped, same reasoning as accountSettings and billingRedeem
  /// below. `routeUrl.billing.account()` already points at this exact path
  /// string (task 4's forward-declare, used by the redeem screen's
  /// post-redemption navigate), so nothing to reconcile there now that this
  /// is a real route.
  accountBilling: app("/account/billing"),
  /// Authenticated, owner-only redeem screen (task 4 of the billing
  /// surfaces plan): the last step of the anonymous LTD purchase journey.
  /// Lives under app-layout like accountSettings, not auth-layout: it needs
  /// the signed-in chrome, and unlike /pricing it is never reachable
  /// signed-out. Account-level, not workspace-scoped, so it sits directly
  /// under app-layout the same way accountSettings does.
  billingRedeem: app("/account/billing/redeem"),
  /// Public LTD sales page, reachable signed-out (task 3 of the billing
  /// surfaces plan). Lives under the chrome-free auth-layout parent, not
  /// app-layout: it needs no ActiveWorkspaceProvider/sidebar and must be
  /// reachable before signup, the same reason /signup and /login sit there.
  pricing: auth("/pricing"),
  /// The one anonymous page in the app (HRMS-REC-012): a stranger applying to
  /// a published opening through its unguessable token. Under auth-layout for
  /// the same reason /pricing is, and one more besides: app-layout mounts the
  /// workspace shell, and this page has no workspace at all. The token is
  /// opaque and carries no personal data, so it belongs in the path (FE-SEC7).
  apply: auth("/apply/$token"),

  /// PAYD-FR-042 (payroll depth H-06). Where a former employee lands.
  ///
  /// Under `auth-layout`, NOT `app-layout`, and that is the point rather than
  /// an implementation detail: an alumnus has no workspace, so there is no
  /// shell to put them in. No sidebar, no workspace switcher, no breadcrumb,
  /// no page they could click into and be refused on. It is account-level and
  /// carries no slug because an alumni session is given none.
  alumni: auth("/alumni"),
  /// Hosted MCP Connect: where the shore's /oauth/authorize sends the browser
  /// so the person approves an AI app. Chrome free like /login, because the
  /// person may arrive signed out and signs in on this same URL.
  oauthConsent: auth("/oauth/consent"),

  auth: {
    signup: auth("/signup"),
    login: auth("/login"),
    forgotPassword: auth("/forgot-password"),
    resetPassword: auth("/reset-password"),
    verify: auth("/auth/verify"),
    authError: auth("/auth/error"),
    acceptInvite: auth("/invitations/accept"),
    googlePickAccount: auth("/auth/google/pick-account"),
  },

  workspace: {
    /// The workspace layout's index route. The `path` (used by `<Link to>`)
    /// is `/$workspaceSlug` (no trailing slash); the `id` (used by
    /// `getRouteApi`) is `/app-layout/$workspaceSlug/` because the route is
    /// registered as a `path: "/"` child of the workspace layout.
    home: {
      path: WORKSPACE_BASE,
      id: `${APP_LAYOUT}${WORKSPACE_BASE}/` as const,
    },
    settings: ws("/settings"),
    settingsCompany: ws("/settings/company"),
    healthConfig: ws("/settings/health-config"),

    people: ws("/people"),
    peopleDetail: ws("/people/$membershipId"),
    peopleEdit: ws("/people/$membershipId/edit"),
    /// Legacy FND-011 path. Redirects to `peopleDetail` with
    /// `?tab=details&section=job`. The path stays so old bookmarks do not 404.
    peopleDetailHr: ws("/people/$membershipId/hr"),
    myTeam: ws("/my-team"),
    orgChart: ws("/org-chart"),

    todos: ws("/todos"),
    todoDetail: ws("/todos/$todoId"),
    adminTodos: ws("/admin/todos"),

    feedback: ws("/feedback"),
    feedbackDetail: ws("/feedback/$feedbackId"),
    adminFeedback: ws("/admin/feedback"),

    goals: ws("/goals"),
    goalDetail: ws("/goals/$goalId"),

    attendance: ws("/attendance"),
    attendanceTeam: ws("/attendance/team"),
    attendanceRequests: ws("/attendance/requests"),
    /// Merged into the team page. Kept in the catalog because the route
    /// still exists as a redirect, so old links do not 404.
    attendanceBoard: ws("/attendance/board"),
    attendanceSetup: ws("/settings/attendance"),

    /// Module 17, HRMS Leave. Employee balance + own requests (Task 2), the
    /// team timeline (Task 6), the balance report (Task 7); `leaveSettings`
    /// lands in Task 8.
    leave: ws("/leave"),
    /// The caller's own requests in full, which `/leave` shows five of.
    ///
    /// A page rather than an expander on `/leave`, because the point of the
    /// 2026-08-20 compression is that `/leave` stays short: an expander puts
    /// it back to its old length on the first click. Deliberately NOT in the
    /// sidebar; it is reached from the link beside the section heading, and
    /// the Leave group already holds four entries.
    ///
    /// Reads the same `useMyLeaveHistory` cache entry `/leave` fills, so the
    /// navigation costs no request. `?period=` carries the selected leave year
    /// across so the two pages cannot disagree about which one they show.
    leaveHistory: ws("/leave/history"),
    /// One row per person, dates across the top, leave drawn as bars. A
    /// timeline rather than attendance's month grid: the question here is
    /// overlap, which bars answer and day cells scatter.
    leaveTeam: ws("/leave/team"),
    /// One row per person, one column per configured leave type. Same
    /// permission ladder as `leaveTeam` (VIEW-5 shares its scope resolver
    /// with VIEW-2): the route itself is reachable by any LEAVE_VIEW_OWN
    /// holder and answers with their own row.
    leaveBalances: ws("/leave/balances"),
    /// Leave types, periods, policies and block lists, plus the year end
    /// rollover. Under /settings alongside attendance's own setup page, not
    /// under /leave: the audience is the admin who configures leave, not the
    /// person who takes it.
    leaveSetup: ws("/settings/leave"),

    /// Payroll configuration: components, structures, and (with PAY-006)
    /// assignments, periods and tax slabs. Under /settings beside the leave and
    /// attendance setup pages for the same reason they are: the audience is the
    /// admin who configures pay, not the person who is paid. The member-facing
    /// payslips page is a separate route and lands with HRMS-PAY-023.
    payrollSetup: ws("/payroll/setup"),

    /// CTC redesign. The HR grid where every member's annual CTC is entered
    /// and revised; it replaces the per-person assignment dialog that used to
    /// live on a Setup tab. Gated on SALARY_ASSIGNMENT_MANAGE_WORKSPACE.
    payrollSalaries: ws("/payroll/salaries"),
    /// HRMS-REC-004, the recruitment planning home. One route with in-page
    /// tabs rather than four sibling routes: the four surfaces are one admin's
    /// single job, and splitting them would put four rows in the sidebar for
    /// a section most workspaces touch once a quarter.
    ///
    /// HRMS-REC-UX P4 rebuilds what this route RENDERS without moving it. The
    /// five-tab strip becomes a jobs list: a job is the thing you open, and
    /// everything about it lives on the job. The path is deliberately unchanged
    /// (`/hiring` was considered and rejected) because renaming it would break
    /// every bookmark and every link already shared with an interviewer, in
    /// exchange for a word. The IA change is what has value; the prefix is not.
    recruitment: ws("/recruitment"),

    /// HRMS-REC-UX P4. One job, and everything about it: its pipeline, its
    /// interviews, its offers, its letters, its own details, behind chips
    /// rather than five sibling tabs at the top of the module.
    ///
    /// This is where the candidate board now lives, scoped to one opening.
    /// The old cross-opening board is deliberately gone: a board spanning
    /// every job answered "where is everyone" for nobody in particular, while
    /// the question a recruiter actually asks is about one role at a time.
    /// The cross-job view that replaced it is the jobs list plus the "needs
    /// you" count, which say which role to open rather than mixing them.
    recruitmentJob: ws("/recruitment/openings/$openingId"),

    /// The three governance and document surfaces that used to be tabs. Each
    /// is workspace-wide rather than per-job, which is why they are siblings
    /// of the jobs list and not chips on a job: a requisition exists before
    /// any opening does, and a staffing plan caps several.
    recruitmentRequisitions: ws("/recruitment/requisitions"),
    recruitmentStaffingPlans: ws("/recruitment/staffing-plans"),
    recruitmentLetters: ws("/recruitment/letters"),
    /// HRMS-REC-011, one candidate in full. A route rather than a drawer over
    /// the board: a recruiter shares this link with an interviewer, and the
    /// stage history and panels are more reading than a drawer holds.
    recruitmentApplicantDetail: ws("/recruitment/applicants/$applicantId"),

    /// HRMS-REC-031, the offers surface. A sibling route rather than a fifth
    /// tab on `recruitment` above: the planning page is where a hire is
    /// budgeted and requested, and this is where it is closed. They are two
    /// ends of the pipeline and a reader arriving at one is rarely after the
    /// other.
    recruitmentOffers: ws("/recruitment/offers"),

    /// HRMS-REC-040, the referrals surface. A sibling route rather than a
    /// fifth tab on `recruitment` above: that page is the hiring admin's
    /// screen, and this one is the only recruitment surface every member can
    /// reach. Folding a member page into an admin tab strip would make the
    /// tabs mean two different things depending on who is reading them.
    recruitmentReferrals: ws("/recruitment/referrals"),

    /// HRMS-REC-021, the interview calendar and the feedback form. A sibling
    /// route rather than a tab on `recruitment` for the same reason referrals
    /// is one: an interviewer holds no admin key and is passed through to
    /// their own rows here, so folding it into the hiring admin's tab strip
    /// would gate the one recruitment page most of the workspace ever opens.
    /// One interview opens in place via `?interviewId=`, not a child route.
    recruitmentInterviews: ws("/recruitment/interviews"),
    /// HRMS-REC-041, one appointment letter as a document. Chromeless
    /// (`wsPrint`), the same archetype the payslip print routes use
    /// (`attachments-and-exports.md` section 9): the page IS the letter, so a
    /// sidebar or a toast would end up on the paper. The shore serializes this
    /// path minus the slug on every letter DTO (`printPath`); the two must
    /// move together.
    recruitmentLetterPrint: wsPrint("/recruitment/appointment-letters/$letterId/print"),

    /// PAYD-FR-001 to 008, FR-026. Every additional salary and named override
    /// for one cycle, one grid. Gated on ADDITIONAL_SALARY_MANAGE_WORKSPACE.
    payrollInputs: ws("/payroll/inputs"),

    /// PAYD-FR-044, FR-045, FR-047, FR-125. Recovery schedules (advances,
    /// standing recoveries, court orders) and the arrears calculator. Reads
    /// gate on PAYROLL_RUN_VIEW_WORKSPACE; writes need
    /// ADDITIONAL_SALARY_MANAGE_WORKSPACE, checked in the route body.
    recoverySchedules: ws("/payroll/recoveries"),

    /// PAYD-FR-030 to FR-034, FR-110, FR-113. The full and final settlement
    /// worksheets: the list, and one worksheet per leaver. Both gate on
    /// FNF_MANAGE_WORKSPACE, statically in the sidebar and again in the route
    /// body (Q11). There is no member-facing settlement route because the
    /// shore has no own-scope settlement endpoint to back one.
    settlements: ws("/payroll/settlements"),
    settlementWorksheet: ws("/payroll/settlements/$settlementId"),

    /// PAYD-FR-050 to FR-065. Statutory compliance document runs (Form 16 and
    /// the rest of a country pack's declared document types): the run list,
    /// and one run's generate, upload, sign and publish page. Both gate on
    /// COMPLIANCE_DOCUMENT_MANAGE_WORKSPACE. There is no member-facing tax
    /// documents route because the shore exposes no own-scope document
    /// endpoint and no download route at all.
    complianceDocuments: ws("/payroll/documents"),
    complianceDocumentRun: ws("/payroll/documents/$runId"),

    /// HRMS-PAY-022, the admin half of a payroll cycle. The list is the launch
    /// pad; the detail route IS the wizard (Define, Review employees, Process,
    /// Resolve, Publish, Pay and export), and every step it offers is derived
    /// from the run's own status, never from where the reader clicked.
    ///
    /// No sidebar entry: frontend-ia CC7 puts Runs behind route tabs on My
    /// payslips, and that page belongs to HRMS-PAY-023. Until those tabs land
    /// this pair is reachable by URL only, the same way `hrSettings` above is.
    payrollRuns: ws("/payroll/runs"),
    payrollRunDetail: ws("/payroll/runs/$runId"),

    /// HRMS-PAY-023. The member half of Payroll: the reader's own released
    /// payslips. ONE lens (`mine`), so no scope tabs; a `?scope=` that means
    /// anything else is normalized back in the route body, and `?slip=` opens
    /// one payslip's sheet, which is where notification deep links land.
    myPayslips: ws("/payroll/payslips"),
    /// ESS-01. Everything a person needs to become payable, in one place:
    /// bank account, statutory ids, tax regime and the declarations that used
    /// to live under My payslips (D10).
    myPayDetails: ws("/payroll/my-details"),

    /// The member's tax year, in two tabs: Declare (the income tax form, one
    /// section per deduction) and Calculator (what those figures are worth,
    /// and the regime comparison). Both write the same declaration row, so the
    /// tab is a view of one thing rather than two features side by side.
    ///
    /// `/payroll/tax-calculator` redirects here with `?tab=calculator`, so the
    /// old bookmarks and the e2e specs keep working.
    taxDeclarations: ws("/payroll/tax-declarations"),

    /// PAYD-FR-010 to FR-016 (payroll depth F-08). Three routes, one job: the
    /// list is every bonus rule in the workspace, the plan route is the editor
    /// AND that plan's batch history, and the batch route is the review grid a
    /// person actually approves from. The batch hangs under its plan in the URL
    /// for the same reason it does on the shore: a batch id alone never
    /// identifies a batch.
    variablePay: ws("/payroll/variable-pay"),
    variablePayPlan: ws("/payroll/variable-pay/$planId"),
    variablePayPayout: ws("/payroll/variable-pay/$planId/batches/$payoutId"),

    /// PAYD-FR-100 to FR-103 (payroll depth K-05). One page for the whole
    /// parallel run: upload a legacy register, map its columns, compare, then
    /// classify what differs. `?import=` names which register is open, so a
    /// link to "the March comparison" is a link somebody can send.
    parallelRun: ws("/payroll/parallel-run"),

    /// PAYD-FR-138 (payroll depth L-06). One member, one window: every hold,
    /// recovery, payment-days change, arrear and transfer that touched their
    /// pay. All three filters live in the URL so a finding is a link somebody
    /// can send rather than a click path to describe.
    payrollMoneyAudit: ws("/payroll/money-audit"),

    /// Flexible benefits: declare a plan, claim against it, watch the balance.
    /// Lifted off My pay details, where the three cards sat below ten others
    /// behind a period picker that fought with the tax regime card's year.
    benefits: ws("/payroll/benefits"),

    /// HRMS-CMP-002. One sidebar entry; the scope tabs (mine, planning,
    /// workspace) live in the URL, not in separate routes. The hub detail
    /// route derives its stepper from the cycle's own stage.
    compensation: ws("/compensation"),
    compCycle: ws("/compensation/cycles/$cycleId"),
    /// The two print views. Chromeless (`wsPrint`): the page is the document.
    /// `payslipPrint` is one payslip; `payrollRunPrint` is every payslip of one
    /// run in sequence, one page each, and is linked (not fetched) from the run
    /// wizard.
    payslipPrint: wsPrint("/payroll/payslips/$slipId/print"),
    payrollRunPrint: wsPrint("/payroll/runs/$runId/print"),

    /// FND-017, the HR settings shell. Designations and grades (FND-013)
    /// mount into it as a named slot; this task only ships the shell and its
    /// own Settings tab. Holiday calendars (FND-015) no longer mount here:
    /// FND-015b task 2 moved that editor onto `/settings/attendance`'s
    /// Calendars tab, and this page now links out to it instead. Nav: none,
    /// matching the story's spec, so no `routeUrl` builder either, nothing
    /// links here yet.
    hrSettings: ws("/settings/hr"),

    /// FND-026, the unified HRMS approvals inbox. One page, one `scope`
    /// search param (`approvals.route.search.ts`) driving for-me / from-me /
    /// workspace, same shape as feedback's and todos' scope-tab pages.
    approvals: ws("/approvals"),

    /// HRMS-EXP-001. One page for the whole expenses module: the `scope`
    /// search param (`expenses.route.search.ts`) drives the claim lenses and
    /// the admin Setup tab, same shape as approvals and feedback. Advances
    /// and travel get their own paths when EXP-012 and EXP-021 land.
    expenses: ws("/expenses"),
    /// HRMS-EXP-004. One claim, on its own page rather than in a sheet over
    /// the list: a claim is read alongside its receipts, its approval and its
    /// history, it is linked to from a notification and from an approval
    /// queue, and all three of those want a URL.
    /// HRMS-EXP-012. Advances are a sibling resource, not a lens on claims:
    /// money asked for before it is spent, with its own statuses and its own
    /// admin actions. Declared BEFORE the claim detail below would match, so
    /// `/expenses/advances` is never read as a claim id.
    expenseAdvances: ws("/expenses/advances"),
    /// HRMS-EXP-021. Authorization for a trip, before the money is spent.
    /// Declared with the other static sibling, above the claim detail.
    expenseTravel: ws("/expenses/travel"),
    expenseClaimDetail: ws("/expenses/$claimId"),

    kudos: ws("/kudos"),
    kudosDetail: ws("/kudos/$kudosId"),

    oneOnOnes: ws("/one-on-ones"),
    oneOnOneDetail: ws("/one-on-ones/$oneOnOneId"),
    /// Relationship history: every 1:1 between the viewer and one person,
    /// recurring and one-off interleaved. Replaced the series page.
    oneOnOneWith: ws("/one-on-ones/with/$userId"),

    notifications: ws("/notifications"),
    notificationsPreferences: ws("/notifications/preferences"),

    pulseSurveys: ws("/pulse-surveys"),
    pulseSurveysNew: ws("/pulse-surveys/new"),
    pulseSurveyDetail: ws("/pulse-surveys/$surveyId"),
    pulseSurveyEdit: ws("/pulse-surveys/$surveyId/edit"),
    pulseSurveyInsights: ws("/pulse-surveys/$surveyId/insights"),
    pulseSurveyRespond: ws("/pulse-surveys/me/deliveries/$deliveryId"),
    myPulse: ws("/my-pulse"),
    /// The member's own completed responses, split off the My surveys page so
    /// that page shows only open work.
    myPulseAnswered: ws("/my-pulse/answered"),
    /// One conversation between a respondent and the workspace admins about a
    /// written answer, opened from My pulse or the reply notification.
    myPulseConversation: ws("/my-pulse/conversations/$threadId"),
    /// The workspace admins' shared inbox of threads on anonymous answers.
    pulseAnswerThreads: ws("/pulse-surveys/answer-threads"),
    /// The workspace admins' view of one thread, opened from the notification
    /// that says an anonymous respondent replied.
    pulseAnswerThread: ws("/pulse-surveys/answer-threads/$threadId"),

    workspaceDashboard: ws("/workspace"),
    workspaceDashboardDept: ws("/workspace/departments/$deptId"),
    workspaceDashboardTheme: ws("/workspace/themes/$theme"),
    workspaceDashboardFeed: ws("/workspace/feed"),
    comparison: ws("/comparison"),
    insights: ws("/insights"),

    actions: ws("/actions"),
    myActions: ws("/actions/my"),
    actionUpdates: ws("/actions/updates"),
    actionDetail: ws("/actions/$actionId"),

    legacyDashboard: ws("/dashboard"),
    legacyDashboardDept: ws("/dashboard/departments/$deptId"),
    legacyDashboardTheme: ws("/dashboard/themes/$theme"),
    legacyDashboardFeed: ws("/dashboard/feed"),

    reviews: ws("/performance-reviews"),
    reviewsCycles: ws("/performance-reviews/cycles"),
    reviewsTemplates: ws("/performance-reviews/templates"),
    reviewsInsights: ws("/performance-reviews/insights"),
    reviewsAppraisalSchedules: ws("/performance-reviews/appraisal-schedules"),
    reviewCycleDetail: ws("/performance-reviews/cycles/$cycleId"),
    reviewManagerRatings: ws("/performance-reviews/cycles/$cycleId/manager-ratings"),
    reviewSelfReview: ws("/performance-reviews/cycles/$cycleId/self-review"),
    reviewManagerReview: ws(
      "/performance-reviews/cycles/$cycleId/participants/$participantId/manager-review",
    ),
    reviewManagerResults: ws(
      "/performance-reviews/cycles/$cycleId/participants/$participantId/results",
    ),
    reviewPeerResponse: ws(
      "/performance-reviews/cycles/$cycleId/participants/$participantId/peer-review",
    ),
    reviewTemplateEditor: ws("/performance-reviews/templates/$templateId"),
    reviewTemplateView: ws("/performance-reviews/templates/$templateId/view"),
  },
} as const;

/**
 * Runtime URL builders. Use ONLY when the slug is a value in scope, not a
 * router param token. TanStack Router's typed `<Link to=...>` should be
 * preferred everywhere else, see `ROUTES.*` above.
 *
 * Examples: notification deep-links built from a server payload,
 * `window.location.assign(...)`, breadcrumb back-links built from
 * `pathname`-parsed slug.
 */
export const routeUrl = {
  workspace: {
    home: (slug: string) => `/${slug}`,
    settings: (slug: string) => `/${slug}/settings`,
    settingsCompany: (slug: string) => `/${slug}/settings/company`,
    healthConfig: (slug: string) => `/${slug}/settings/health-config`,
  },
  billing: {
    /// Forward-declared for task 4 (a successful redemption navigates
    /// here): task 5 registers the actual `/account/billing` route. Same
    /// pattern as `reviews.templateEditor` below, which forward-declared a
    /// route it didn't register until a later task. Account-level, no
    /// workspace slug, so unlike every other builder here this takes no
    /// argument.
    account: () => "/account/billing",
  },
  feedback: {
    list: (slug: string) => `/${slug}/feedback`,
    detail: (slug: string, id: string) => `/${slug}/feedback/${id}`,
  },
  /// FND-026. No `detail` builder: the inbox has no per-request page, the
  /// shore's own `deepLink` field on each row already carries the full route
  /// string for "view this request" links.
  approvals: {
    list: (slug: string) => `/${slug}/approvals`,
  },
  attendance: {
    mine: (slug: string) => `/${slug}/attendance`,
    team: (slug: string) => `/${slug}/attendance/team`,
    board: (slug: string) => `/${slug}/attendance/board`,
    setup: (slug: string) => `/${slug}/settings/attendance`,
    /// An `<a href>` target only (see `payroll.setupTab`): the query string makes it unsafe as a
    /// `<Link to>` value.
    setupTab: (slug: string, tab: "calendars" | "shifts") =>
      `/${slug}/settings/attendance?tab=${tab}`,
  },
  leave: {
    mine: (slug: string) => `/${slug}/leave`,
    history: (slug: string) => `/${slug}/leave/history`,
    team: (slug: string) => `/${slug}/leave/team`,
    /// Same route as `team`, opened on the "To decide" tab. Notification
    /// deep-links use this one: the recipients of a filed leave application
    /// are the approvers, and this tab is the module's own decision queue.
    /// The only `routeUrl` builder that embeds a query string. Verified
    /// against TanStack Router 1.169.2: `buildLocation` puts the whole
    /// string into `pathname` with an empty `searchStr`, so
    /// `getMatchedRoutes` on that pathname does not match `/leave/team`.
    /// It only works today because the sole consumer, the notification row's
    /// `navigate({ to: href })`, goes through `commitLocation`, which pushes
    /// the href into history and lets the router re-parse it from scratch.
    /// Not safe as a `<Link to>` value (preloading calls `buildLocation`
    /// directly) or for any other consumer that calls `buildLocation`
    /// without going through history.
    decide: (slug: string) => `/${slug}/leave/team?tab=decide`,
    setup: (slug: string) => `/${slug}/settings/leave`,
    /// An `<a href>` target only, like `attendance.setupTab`. Types are a view of the Plans tab.
    setupTab: (
      slug: string,
      tab: "plans" | "calendar",
      plansView?: "plans" | "types",
      calendarView?: "years" | "blocked-dates" | "rollover",
    ) =>
      `/${slug}/settings/leave?tab=${tab}${plansView ? `&plansView=${plansView}` : ""}${calendarView ? `&calendarView=${calendarView}` : ""}`,
  },

  /// HRMS-PAY-022. Runtime builders because the run wizard's resolve step
  /// deep-links OUT of itself to the surface that fixes each failure, and
  /// those links are built from a reason string, not from a typed route call
  /// site. `setupTab` embeds a query string for the same reason
  /// `leave.decide` does; unlike that one it is only ever an `<a href>`
  /// (a document load out of the wizard), never a `<Link to>` value, so the
  /// `buildLocation` caveat documented there does not apply.
  payroll: {
    runs: (slug: string) => `/${slug}/payroll/runs`,
    run: (slug: string, runId: string) => `/${slug}/payroll/runs/${runId}`,
    runPrint: (slug: string, runId: string) => `/${slug}/payroll/runs/${runId}/print`,
    setup: (slug: string) => `/${slug}/payroll/setup`,
    setupTab: (slug: string, tab: "components" | "structures" | "assignments") =>
      `/${slug}/payroll/setup?tab=${tab}`,
    setupStructure: (slug: string, structureId: string) =>
      `/${slug}/payroll/setup?tab=structures&structureId=${structureId}`,
    salaries: (
      slug: string,
      opts?: { coverCycle?: string; filter?: "missing"; member?: string },
    ) => {
      const path = `/${slug}/payroll/salaries`;
      const params = new URLSearchParams();
      if (opts?.coverCycle) params.set("coverCycle", opts.coverCycle);
      if (opts?.filter) params.set("filter", opts.filter);
      if (opts?.member) params.set("member", opts.member);
      const qs = params.toString();
      return qs ? `${path}?${qs}` : path;
    },
    /// Payroll depth C-15. The run wizard's Close inputs step links out to the
    /// cycle it is closing, so the month rides the query string exactly as
    /// `payroll-inputs.route.search.ts` parses it (`YYYY-MM`).
    inputs: (slug: string, month?: string) =>
      month === undefined ? `/${slug}/payroll/inputs` : `/${slug}/payroll/inputs?month=${month}`,
    recoverySchedules: (slug: string) => `/${slug}/payroll/recoveries`,
    settlements: (slug: string, opts?: { member?: string }) => {
      const path = `/${slug}/payroll/settlements`;
      const params = new URLSearchParams();
      if (opts?.member) params.set("member", opts.member);
      const qs = params.toString();
      return qs ? `${path}?${qs}` : path;
    },
    myDetails: (slug: string) => `/${slug}/payroll/my-details`,
    taxCalculator: (slug: string) => `/${slug}/payroll/tax-calculator`,
    /// The tab is optional: a plain link lands on Declare, which is what most
    /// people came to do.
    taxDeclarations: (slug: string, tab?: "declare" | "calculator") =>
      tab === undefined
        ? `/${slug}/payroll/tax-declarations`
        : `/${slug}/payroll/tax-declarations?tab=${tab}`,
    benefits: (slug: string) => `/${slug}/payroll/benefits`,
    /// Payroll depth F-08 and K-05. Runtime builders because the plan list, the
    /// plan editor and the batch grid link to each other with ids that come
    /// from data rather than from a typed route call site.
    variablePay: (slug: string) => `/${slug}/payroll/variable-pay`,
    variablePayPlan: (slug: string, planId: string) => `/${slug}/payroll/variable-pay/${planId}`,
    variablePayPayout: (slug: string, planId: string, payoutId: string) =>
      `/${slug}/payroll/variable-pay/${planId}/batches/${payoutId}`,
    parallelRun: (slug: string, importId?: string) =>
      importId === undefined
        ? `/${slug}/payroll/parallel-run`
        : `/${slug}/payroll/parallel-run?import=${importId}`,
    /// Payroll depth L-06. The membership id is required: the audit answers
    /// one person at a time, so a link without one is a link to a prompt.
    payrollMoneyAudit: (slug: string, membershipId: string) =>
      `/${slug}/payroll/money-audit?membership=${membershipId}`,
    /// Built here rather than by hand at each call site (RT1). The optional
    /// slip id is the notification deep link's `?slip=`, which opens that row
    /// in the detail sheet.
    myPayslips: (slug: string, slipId?: string) =>
      slipId === undefined
        ? `/${slug}/payroll/payslips`
        : `/${slug}/payroll/payslips?slip=${slipId}`,
  },
  kudos: {
    list: (slug: string) => `/${slug}/kudos`,
    detail: (slug: string, id: string) => `/${slug}/kudos/${id}`,
  },
  todos: {
    list: (slug: string) => `/${slug}/todos`,
    detail: (slug: string, id: string) => `/${slug}/todos/${id}`,
  },
  goals: {
    list: (slug: string) => `/${slug}/goals`,
    detail: (slug: string, id: string) => `/${slug}/goals/${id}`,
  },
  oneOnOnes: {
    list: (slug: string) => `/${slug}/one-on-ones`,
    detail: (slug: string, id: string) => `/${slug}/one-on-ones/${id}`,
  },
  pulseSurveys: {
    respond: (slug: string, deliveryId: string) =>
      `/${slug}/pulse-surveys/me/deliveries/${deliveryId}`,
    answerThread: (slug: string, threadId: string) =>
      `/${slug}/pulse-surveys/answer-threads/${threadId}`,
  },
  pulse: {
    insights: (slug: string) => `/${slug}/insights`,
    mine: (slug: string) => `/${slug}/my-pulse`,
    conversation: (slug: string, threadId: string) => `/${slug}/my-pulse/conversations/${threadId}`,
    /// The insights queue with one insight open in its detail sheet.
    insight: (slug: string, insightId: string) =>
      `/${slug}/insights?tab=queue&insightId=${encodeURIComponent(insightId)}`,
  },
  actions: {
    detail: (slug: string, id: string) => `/${slug}/actions/${id}`,
    /// Every role can open "My actions"; the workspace list is admin-only.
    mine: (slug: string) => `/${slug}/actions/my`,
  },
  people: {
    list: (slug: string) => `/${slug}/people`,
    detail: (slug: string, membershipId: string) => `/${slug}/people/${membershipId}`,
    edit: (slug: string, membershipId: string) => `/${slug}/people/${membershipId}/edit`,
    hr: (slug: string, membershipId: string) => `/${slug}/people/${membershipId}/hr`,
  },
  reviews: {
    list: (slug: string) => `/${slug}/performance-reviews`,
    cycles: (slug: string) => `/${slug}/performance-reviews/cycles`,
    templates: (slug: string) => `/${slug}/performance-reviews/templates`,
    insights: (slug: string) => `/${slug}/performance-reviews/insights`,
    appraisalSchedules: (slug: string) => `/${slug}/performance-reviews/appraisal-schedules`,
    cycleDetail: (slug: string, cycleId: string) =>
      `/${slug}/performance-reviews/cycles/${cycleId}`,
    managerRatings: (slug: string, cycleId: string) =>
      `/${slug}/performance-reviews/cycles/${cycleId}/manager-ratings`,
    selfReview: (slug: string, cycleId: string) =>
      `/${slug}/performance-reviews/cycles/${cycleId}/self-review`,
    managerReview: (slug: string, cycleId: string, participantId: string) =>
      `/${slug}/performance-reviews/cycles/${cycleId}/participants/${participantId}/manager-review`,
    managerResults: (slug: string, cycleId: string, participantId: string) =>
      `/${slug}/performance-reviews/cycles/${cycleId}/participants/${participantId}/results`,
    peerResponse: (slug: string, cycleId: string, participantId: string) =>
      `/${slug}/performance-reviews/cycles/${cycleId}/participants/${participantId}/peer-review`,
    /// Forward-declared for Task 5 (bank navigation). Task 6 registers the actual route.
    templateEditor: (slug: string, templateId: string) =>
      `/${slug}/performance-reviews/templates/${templateId}`,
    templateView: (slug: string, templateId: string) =>
      `/${slug}/performance-reviews/templates/${templateId}/view`,
  },

  /// HRMS-REC-UX P4. The four peer surfaces of the hiring module. Runtime
  /// builders because the shared strip in `recruitment-page-shell.tsx`
  /// resolves them against the active slug and compares them to the live
  /// pathname to mark the current one, which needs values, not `$slug`
  /// tokens.
  recruitment: {
    home: (slug: string) => `/${slug}/recruitment`,
    requisitions: (slug: string) => `/${slug}/recruitment/requisitions`,
    staffingPlans: (slug: string) => `/${slug}/recruitment/staffing-plans`,
    letters: (slug: string) => `/${slug}/recruitment/letters`,
    offers: (slug: string) => `/${slug}/recruitment/offers`,
  },
} as const;
